/**
 * COMPACT TEMPLATE — PersonalExperienceSection
 *
 * ⚠️  DO NOT add data-splitable-item / data-splitable-header attributes here.
 * The compact template uses CSS-based page splitting, not the React
 * usePageDelimitation system. Adding split attributes will break the
 * sidebar/main layout by pushing content to wrong pages.
 *
 * History: commit dc115433 added chunk slicing + data-splitable attributes
 * to this component as part of wiring usePageDelimitation into compact.
 * This broke the split — reverted to the simple un-chunked version.
 */
import { type CVEditingSection, type FullCV } from '@cibli/shared';

import { useCVTranslation } from '@/features/cv/hooks/useCVTranslation.ts';

import EditableSection from '../../../components/form/EditableSection';
import { formatDateRange, parseMissions } from './helpers';

interface PersonalExperienceSectionProps {
  experiences: FullCV['personalExperiences'];
  onEdit?: (section: CVEditingSection) => void;
}

export default function PersonalExperienceSection({
  experiences,
  onEdit,
}: PersonalExperienceSectionProps) {
  const { t } = useCVTranslation();
  const editable = onEdit !== undefined;

  if (!experiences?.length && !editable) return null;

  const handleEdit = () => {
    if (onEdit) onEdit('personalExperiences');
  };

  return (
    <EditableSection onEdit={handleEdit} editable={editable} splitable>
      <div className="mb-1 mx-0 px-0">
        <h2 className="text-[11px] font-black uppercase tracking-widest mb-0.25">
          {t('CV.personalExperiences')}
        </h2>
        {(experiences ?? []).map((exp, index) => {
          const dateRange = formatDateRange(exp.startDate, exp.endDate, t);
          const missions = parseMissions(exp.description);

          return (
            <div key={index} className="mb-3 break-inside-avoid [page-break-inside:avoid]">
              <div className="flex items-baseline justify-between gap-2 mb-0.5">
                <h3 className="text-[11px] font-black uppercase text-black min-w-0">
                  {(exp.title ?? '').toUpperCase()}
                </h3>
                {dateRange && (
                  <p className="text-[10.75px] text-gray-600 italic whitespace-nowrap flex-shrink-0">
                    {dateRange}
                  </p>
                )}
              </div>
              {missions.length > 0 && (
                <ul className="ml-3 list-disc">
                  {missions.map((mission, i) => (
                    <li
                      key={i}
                      className="text-[10.75px] text-black leading-snug mb-0.5 text-justify"
                    >
                      {mission}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </EditableSection>
  );
}
